import Vue from 'vue'
import { i18n } from '@/plugins/i18n'
import { agreementService } from '@/services/api'
import { groupByArrayOfObject } from '@/common/arrays'

const initialStates = {
    status: 'LOADING',
    agreementsList: [],
    pagination: {
        no_paginate: false,
        per_page: 20,
        current_page: 1,
        last_page: 1,
    },
    params: {
        page: 1
    }
}

const mutations = {
    loading(state) {
        state.status = 'LOADING'
    },
    setAgreementsList(state, agreements) {
        state.status = 'SET_LIST'
        state.agreementsList = agreements
    },
    setPagination(state, pagination) {
        state.status = 'SET_PAGINATION'
        state.pagination = Object.assign({}, state.pagination, pagination)
    },
    setParams(state, params) {
        state.status = 'SET_PARAMS'
        state.params = Object.assign({}, state.params, params)
    },
    createAgreement(state, agreement) {
        state.status = 'CREATE'
        state.agreementsList[agreement.id] = agreement
    },
    updateAgreement(state, agreement) {
        state.status = 'UPDATE'
        state.agreementsList[agreement.id] = Object.assign({}, state.agreementsList[agreement.id], agreement)
    },
    deleteAgreement(state, id) {
        state.status = 'DELETE_FROM_LIST'

        Vue.delete(state.agreementsList, id)
    },
    error(state) {
        state.status = 'ERROR'
    }
}

const getters = {
    status: state => state.status,
    params: state => state.params,
    agreements: state => state.agreementsList,
    pagination: state => state.pagination,
    agreementById: state => id => state.agreementsList[id],
    selectableAgreements: (state, getters) => {
        return Object.keys(getters.agreements).map((id) => {
            return {
                value: getters.agreements[id].id,
                label: getters.agreements[id].title,
            }
        })
    },
    selectableAgreementsWithNull: (state, getters) => {
        return [
            {
                label: i18n.t('select.placeholder'),
                value: ''
            },
            ...getters.selectableAgreements
        ]
    }
}

const actions = {
    setParamsAndFetch({dispatch}, params) {
        dispatch('setParams', params)
        dispatch('fetch')
    },
    setParams({commit}, params) {
        commit('setParams', params)
    },
    fetch({commit, state}, params) {
        params = Object.assign({}, state.params, params)

        commit('loading')

        return new Promise((resolve, reject) => {
            agreementService.fetch(params)
                .then(response => {
                    const result = response.data
                    const agreements = groupByArrayOfObject(result.data, 'id')

                    commit('setAgreementsList', agreements)
                    if (params.no_paginate) {
                        commit('setPagination', {
                            no_paginate: true
                        })
                    } else {
                        commit('setPagination', {
                            per_page: result.per_page,
                            current_page: result.current_page,
                            last_page: result.last_page,
                            no_paginate: false,
                        })
                        commit('setParams', {
                            page: result.current_page,
                        })
                    }
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error)
                })
        })
    },
    create({commit}, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            agreementService.create(params)
                .then(response => {
                    const result = response.data

                    commit('createAgreement', result.data)
                    resolve(result)
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    edit({commit}, params) {
        commit('loading')

        return new Promise((resolve, reject) => {
            agreementService.edit(params, params.id)
                .then(() => {
                    commit('updateAgreement', params)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
    delete({ commit }, hashid) {
        commit('loading')

        return new Promise((resolve, reject) => {
            agreementService.delete(hashid)
                .then(() => {
                    commit('deleteAgreement', hashid)
                    resolve()
                })
                .catch(error => {
                    commit('error')
                    reject(error.response ? error.response.data : error.response)
                })
        })
    },
}

export default {
    namespaced: true,
    state: initialStates,
    mutations: mutations,
    getters: getters,
    actions: actions,
}